/**
 * Teacher appreciation tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const teacherAppreciationTagsPack: ToilePack = {
    slug: 'teacher-appreciation-tags',
    category: 'season',
    name: 'Teacher appreciation tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pen', label: 'Pen tag', subject: 'a sheet of six tags reading "Thanks for helping me grow write" with "grow" crossed out, a small pen drawn beside the words, a punched hole at the top'},
        {key: 'sweets', label: 'Sweet treat', subject: 'a sheet of round tags reading "You\'re one smart cookie" with a drawn cookie and a "From:" line below'},
        {key: 'plant', label: 'Plant tag', subject: 'a sheet of tags reading "Thank you for helping me grow" with a sprouting seedling in a pot, and a blank line for the pupil\'s name'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'tags in white and dark ink as if printed on kraft paper, hand-stamped look, simple line motifs', palette: 'kraft brown with white and dark brown ink', framing: 'Flat on the sheet, evenly spaced'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'apple red, yellow, navy and cream, four colors', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'lineart', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills, simple hand lettering', palette: 'black ink only', framing: 'Drawn on plain white paper with a clean margin'},
    ],
    directions: ['Gift card', 'Coffee mug', 'Gold ink'],
    directionHint: 'try: Gift card',
    seo: {
        title: 'Teacher Appreciation Tags: Pen, Sweets, Plant',
        metaDescription: 'Printable teacher gift tags with a pen pun, a cookie joke and a plant thank-you, cut out and tied on, in three styles.',
        h1: 'Teacher appreciation tags: pen, sweet treat, plant',
        tagline: "Nine sheets of cut-out tags for a pen, a jar of sweets or a plant, each with a line for the child's name.",
        intro: "Printable tags a parent ties to a teacher gift, such as a pen, a plant or a jar of sweets. Each sheet is a row of cut-out tags with a thank-you or a pun: a pen tag that plays on writing, a plant tag above a seedling. Most tags have a line for the child's name. Kraft paper, flat color or line art. Type a different gift or pun, and the same tag carries it.",
        useCases: [
            'a tag on a pen given at the end of the school year',
            'a tag on a potted plant',
            'a tag on a jar of sweets',
            'a tag on a gift card',
        ],
        faq: [
            {q: "Is there a line for the child's name?", a: "Most tags have a blank line for the pupil's name or a From line."},
            {q: 'What are the styles?', a: 'Kraft paper uses white and dark ink on brown paper, flat color uses four bold colors, and line art is fine black outline on white.'},
            {q: 'Can the pun be changed?', a: 'Yes. Type a different pun or thank-you, and the same tag layout carries it.'},
        ],
    },
}
